// ** React Imports **
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

// ** Third Party **
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useQuery } from "react-query";
import { useSnackbar } from 'notistack';

// ** Material-UI **
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, ListSubheader } from "@mui/material";

// ** API **
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

// ** Components **
import StepProgressIndicator from '../../../components/authentication/StepProgressIndicator';
import LoadingOverlay from '../../../components/authentication/LoadingOverlay';
import InputField from '../../../components/authentication/InputField';

// ** Styles **
import './RegisterStepBusinessInfo.css';

/**
 * RegisterStepBusinessInfo Component (Step 1)
 * 
 * Business registration form - collects business details
 * 
 * Fields:
 * - designation
 * - business name
 * - business category
 * - business email
 * - business contact number
 * - VAT number
 * - tax reference number
 * - business registration number
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.userData - User data from Step 0
 * @param {Object} props.initialData - Pre-filled data if returning to this step
 * @param {Function} props.onComplete - Callback when step completed
 */
const RegisterStepBusinessInfo = ({ userData, initialData, onComplete, onStepClick }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ** React Hook Form **
  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      designation: initialData?.designation || "",
      name: initialData?.name || "",
      businessCategory: initialData?.businessCategory || "",
      email: initialData?.email || userData?.email || "",
      mobile: initialData?.mobile || "+91",
      vatNumber: initialData?.vatNumber || "",
      taxRefNumber: initialData?.taxRefNumber || "",
      registrationNumber: initialData?.registrationNumber || "",
    },
  });

  const formValues = watch();

  // ** Fetch Business Categories **
  const { data: businessCategories, isLoading: categoriesLoading } = useQuery(
    ["businessCategories"],
    async () => {
      const res = await axiosInstance.get(`${ApiEndPoints.BUSINESS.category}`);
      return res.data;
    }
  );

  /**
   * Handle form submission
   * Validates and moves to next step
   */
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      // Basic validation
      if (!data.designation || !data.name || !data.businessCategory || !data.email || !data.mobile) {
        enqueueSnackbar("please fill all required fields", {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 3000,
        });
        setIsSubmitting(false);
        return;
      }

      // Move to next step with business data
      onComplete(data);
    } catch (error) {
      console.error("Business info error:", error);
      enqueueSnackbar("please check all fields and try again", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-step-business">
      <div className="register-step-business__card" style={{ position: 'relative' }}>
        {/* Loading Overlay - Shows during business info processing */}
        <LoadingOverlay 
          isVisible={isSubmitting} 
          customMessages={[
            'processing your business information...',
            'setting things up...',
            'almost there...'
          ]}
        />
        
        {/* Progress Indicator */}
        <StepProgressIndicator currentStep={1} totalSteps={3} onStepClick={onStepClick} />

        {/* Title */}
        <h2 className="register-step-business__title">business registration</h2>

        {/* Form */}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="register-step-business__form">
          
          {/* Designation */}
          <InputField
            {...register("designation", { required: "designation is required" })}
            type="text"
            placeholder="designation*"
            value={formValues.designation}
            error={errors.designation ? errors.designation.message : ''}
            required
          />

          {/* Business Name */}
          <InputField
            {...register("name", { required: "business name is required" })}
            type="text"
            placeholder="business name*"
            value={formValues.name}
            error={errors.name ? errors.name.message : ''}
            required
          />

          {/* Business Category */}
          <div className="register-step-business__field">
            <Controller
              name="businessCategory"
              control={control}
              rules={{ required: "business category is required" }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  error={!!errors.businessCategory}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "10px",
                      background: "#F4F5FA",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ECECEC",
                    },
                    "& .MuiFormLabel-root": {
                      fontSize: "16px",
                      color: "#888",
                    },
                  }}
                >
                  <InputLabel>business category</InputLabel>
                  <Select
                    {...field}
                    label="business category"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 2,
                          mt: 1,
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {categoriesLoading ? (
                      <MenuItem disabled>loading categories...</MenuItem>
                    ) : (
                      businessCategories?.map((category) => [
                        <ListSubheader
                          key={`header-${category.id}`}
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          {category.name}
                        </ListSubheader>,
                        ...(category.child || []).map((childCategory) => (
                          <MenuItem
                            key={childCategory.id}
                            value={childCategory.id}
                            sx={{
                              pl: 4,
                              "&:hover": {
                                backgroundColor: "#f0f0f0",
                              },
                            }}
                          >
                            {childCategory.name}
                          </MenuItem>
                        )),
                      ])
                    )}
                  </Select>
                  <FormHelperText>
                    {errors.businessCategory?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </div>

          {/* Business Email */}
          <InputField
            {...register("email", { 
              required: "business email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "invalid email address"
              }
            })}
            type="email"
            placeholder="business email*"
            value={formValues.email}
            error={errors.email ? errors.email.message : ''}
            required
          />

          {/* Business Contact Number */}
          <div className="register-step-business__field">
            <Controller
              name="mobile"
              control={control}
              rules={{ required: "business contact number is required" }}
              render={({ field }) => (
                <PhoneInput
                  {...field}
                  country={'in'}
                  enableSearch={true}
                  inputClass="register-step-business__phone-input"
                  containerClass="register-step-business__phone-container"
                  buttonClass="register-step-business__phone-button"
                  searchClass="register-step-business__phone-search"
                  placeholder="+27 business contact number*"
                />
              )}
            />
            {errors.mobile && (
              <span className="register-step-business__error">
                {errors.mobile.message}
              </span>
            )}
          </div>

          {/* VAT Number */}
          <InputField
            {...register("vatNumber")}
            type="text"
            placeholder="vat number (optional)"
            value={formValues.vatNumber}
          />

          {/* Tax Reference Number */}
          <InputField
            {...register("taxRefNumber")}
            type="text"
            placeholder="tax reference number (optional)"
            value={formValues.taxRefNumber}
          />

          {/* Business Registration Number */}
          <InputField
            {...register("registrationNumber")}
            type="text"
            placeholder="business registration number (optional)"
            value={formValues.registrationNumber}
          />

          {/* Continue Button */}
          <button
            type="submit"
            className="register-step-business__submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'processing...' : 'continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegisterStepBusinessInfo;


